import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, LogOut, Bell, HelpCircle, Star, ChevronRight, Pencil, Smartphone, Link2, Palette, ImagePlus, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { upsertCouple, invitationUrl, uploadCover, deleteCover, coverUrl } from '../api';
import { THEMES } from '../themes';

export default function Profil() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, couple, setCouple, logout } = useAuth();
  const [dlg, setDlg] = useState(false);
  const [dlgTheme, setDlgTheme] = useState(false);
  const [theme, setTheme] = useState('klasik');
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({});

  const openEdit = () => { setForm({ ...couple }); setDlg(true); };
  const openTheme = () => { if (!couple?.slug) { toast({ title: 'Lengkapi data pasangan dulu' }); return; } setTheme(couple?.theme || 'klasik'); setDlgTheme(true); };
  const save = async () => {
    try {
      const payload = { ...form, totalBudget: Number(form.totalBudget)||0 };
      const saved = await upsertCouple(payload); setCouple({ ...couple, ...saved }); setDlg(false);
      toast({ title: 'Data diperbarui' });
    } catch (e) { toast({ title: 'Gagal', variant: 'destructive' }); }
  };
  const saveTheme = async () => {
    try {
      const saved = await upsertCouple({ ...couple, theme, totalBudget: Number(couple.totalBudget) || 0 });
      setCouple({ ...couple, ...saved }); setDlgTheme(false); toast({ title: `Tema "${THEMES[theme].name}" dipakai ✨` });
    } catch (e) { toast({ title: 'Gagal', variant: 'destructive' }); }
  };
  const pickCover = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast({ title: 'Ukuran maksimal 5MB', variant: 'destructive' }); return; }
    setUploading(true);
    try { const r = await uploadCover(f); setCouple({ ...couple, coverFileId: r.coverFileId }); toast({ title: 'Foto cover diperbarui' }); }
    catch (err) { toast({ title: 'Gagal unggah', description: err?.response?.data?.detail || 'Coba lagi', variant: 'destructive' }); }
    finally { setUploading(false); e.target.value = ''; }
  };
  const removeCover = async () => { await deleteCover(); const c = { ...couple }; delete c.coverFileId; setCouple(c); toast({ title: 'Foto cover dihapus' }); };

  const installApp = async () => {
    const evt = window.__npInstallPrompt;
    if (evt) { evt.prompt(); const { outcome } = await evt.userChoice; if (outcome === 'accepted') { window.__npInstallPrompt = null; toast({ title: 'Aplikasi terpasang! 🎉' }); } return; }
    if (window.matchMedia('(display-mode: standalone)').matches) { toast({ title: 'Aplikasi sudah terpasang di perangkat ini' }); return; }
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    toast({ title: 'Cara install di HP', description: ios ? 'Safari: ketuk tombol Bagikan → "Tambah ke Layar Utama".' : 'Buka menu browser (⋮) → "Install app" atau "Tambahkan ke layar utama".' });
  };

  const menu = [
    { icon: Pencil, label: 'Edit data pasangan & undangan', color: 'text-terracotta-500', bg: 'bg-terracotta-50', onClick: openEdit, testid: 'edit-couple-button' },
    { icon: Link2, label: 'Lihat undangan digital', color: 'text-sage-600', bg: 'bg-sage-50', onClick: () => couple?.slug ? window.open(invitationUrl(couple.slug), '_blank') : toast({ title: 'Lengkapi data pasangan dulu' }), testid: 'view-invitation-button' },
    { icon: Palette, label: 'Tema & foto undangan', color: 'text-terracotta-500', bg: 'bg-terracotta-50', onClick: openTheme, testid: 'theme-invitation-button' },
    { icon: Smartphone, label: 'Install aplikasi di HP', color: 'text-terracotta-500', bg: 'bg-terracotta-50', onClick: installApp, testid: 'install-app-button' },
    { icon: Bell, label: 'Notifikasi', color: 'text-sage-600', bg: 'bg-sage-50', onClick: () => toast({title:'Pengaturan notifikasi'}) },
    { icon: Star, label: 'Beri rating aplikasi', color: 'text-terracotta-500', bg: 'bg-terracotta-50', onClick: () => toast({title:'Terima kasih! ⭐'}) },
    { icon: HelpCircle, label: 'Bantuan & FAQ', color: 'text-sage-600', bg: 'bg-sage-50', onClick: () => toast({title:'Menuju halaman bantuan'}) }
  ];

  const akadDate = couple?.akadDate ? new Date(couple.akadDate).toLocaleDateString('id-ID', { day:'numeric', month:'long', year:'numeric' }) : '-';

  return (
    <div className="px-5 pt-8 lg:pt-10">
      <h1 className="font-serif text-4xl text-sage-700 mb-6">Profil</h1>

      <div className="bg-gradient-to-br from-terracotta-500 to-terracotta-600 rounded-3xl p-7 text-white mb-5 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 70% 30%, white 1px, transparent 1px)', backgroundSize: '24px 24px'}} />
        <div className="relative">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" fill="white" />
            </div>
            <div className="text-xs tracking-widest text-cream-100">PASANGAN BAHAGIA</div>
          </div>
          <div className="font-serif text-4xl">{couple?.brideName || '...'} &amp; {couple?.groomName || '...'}</div>
          <div className="text-cream-100 text-sm mt-2">Menikah {akadDate}</div>
          {couple?.venue && <div className="text-cream-100 text-xs mt-1">{couple.venue}</div>}
        </div>
      </div>

      <div className="bg-white rounded-2xl p-5 border border-cream-200 mb-5">
        <div className="flex justify-between text-sm mb-3"><div className="text-sage-500">Nama</div><div className="text-sage-700 font-medium">{user?.name || '-'}</div></div>
        <div className="flex justify-between text-sm mb-3"><div className="text-sage-500">Email</div><div className="text-sage-700 font-medium">{user?.email || '-'}</div></div>
        <div className="flex justify-between text-sm mb-3"><div className="text-sage-500">Target Budget</div><div className="text-sage-700 font-medium">Rp {Number(couple?.totalBudget || 0).toLocaleString('id-ID')}</div></div>
        <div className="flex justify-between text-sm"><div className="text-sage-500">Paket</div><div className={`font-medium ${user?.is_paid ? 'text-terracotta-500' : 'text-sage-400'}`} data-testid="package-status">{user?.is_paid ? 'Lengkap ✨ · Seumur hidup' : 'Belum aktif'}</div></div>
      </div>

      <div className="bg-white rounded-2xl border border-cream-200 divide-y divide-cream-100 mb-5">
        {menu.map((m, i) => {
          const Icon = m.icon;
          return (
            <button key={i} onClick={m.onClick} data-testid={m.testid} className="w-full px-5 py-4 flex items-center gap-4 hover:bg-cream-50 transition-colors text-left">
              <div className={`w-9 h-9 rounded-xl ${m.bg} flex items-center justify-center`}><Icon className={`w-4 h-4 ${m.color}`} /></div>
              <div className="flex-1 text-sm text-sage-700 font-medium">{m.label}</div>
              <ChevronRight className="w-4 h-4 text-sage-400" />
            </button>
          );
        })}
      </div>

      <Button onClick={logout} variant="outline" className="w-full h-11 rounded-full border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600">
        <LogOut className="w-4 h-4 mr-2" /> Keluar
      </Button>

      <div className="text-center text-xs text-sage-400 mt-6">Naik Pelaminan v1.0 · Made with ❤️</div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-sage-700">Edit Data Pasangan & Undangan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Mempelai wanita</Label><Input value={form.brideName||''} onChange={(e)=>setForm({...form, brideName:e.target.value})} className="mt-1 bg-cream-50" /></div>
              <div><Label>Mempelai pria</Label><Input value={form.groomName||''} onChange={(e)=>setForm({...form, groomName:e.target.value})} className="mt-1 bg-cream-50" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tanggal akad</Label><Input type="date" value={form.akadDate||''} onChange={(e)=>setForm({...form, akadDate:e.target.value})} className="mt-1 bg-cream-50" /></div>
              <div><Label>Jam akad</Label><Input type="time" value={form.akadTime||''} onChange={(e)=>setForm({...form, akadTime:e.target.value})} className="mt-1 bg-cream-50" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tanggal resepsi</Label><Input type="date" value={form.resepsiDate||''} onChange={(e)=>setForm({...form, resepsiDate:e.target.value})} className="mt-1 bg-cream-50" /></div>
              <div><Label>Jam resepsi</Label><Input type="time" value={form.resepsiTime||''} onChange={(e)=>setForm({...form, resepsiTime:e.target.value})} className="mt-1 bg-cream-50" /></div>
            </div>
            <div><Label>Venue</Label><Input value={form.venue||''} onChange={(e)=>setForm({...form, venue:e.target.value})} className="mt-1 bg-cream-50" /></div>
            <div><Label>Alamat venue</Label><Input value={form.venueAddress||''} onChange={(e)=>setForm({...form, venueAddress:e.target.value})} placeholder="Jl. ... No. ..., Kota" className="mt-1 bg-cream-50" /></div>
            <div><Label>Link Google Maps</Label><Input value={form.mapsUrl||''} onChange={(e)=>setForm({...form, mapsUrl:e.target.value})} placeholder="https://maps.app.goo.gl/..." className="mt-1 bg-cream-50" /></div>
            <div><Label>Kata sambutan undangan</Label><Textarea value={form.greeting||''} onChange={(e)=>setForm({...form, greeting:e.target.value})} placeholder="Kosongkan untuk pakai teks standar" className="mt-1 bg-cream-50 min-h-[80px]" /></div>
            <div><Label>Total budget (Rp)</Label><Input type="number" value={form.totalBudget||''} onChange={(e)=>setForm({...form, totalBudget:e.target.value})} className="mt-1 bg-cream-50" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-couple-button" className="bg-terracotta-500 hover:bg-terracotta-600 text-white">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlgTheme} onOpenChange={setDlgTheme}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="theme-dialog">
          <DialogHeader><DialogTitle className="font-serif text-sage-700">Tema & Foto Undangan</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div>
              <Label className="text-sage-600">Foto cover</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-28 h-20 rounded-2xl overflow-hidden bg-cream-100 border border-cream-200 flex items-center justify-center flex-shrink-0">
                  {coverUrl(couple) ? <img src={coverUrl(couple)} alt="Cover" className="w-full h-full object-cover" data-testid="cover-preview" /> : <ImagePlus className="w-6 h-6 text-sage-300" />}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white text-sm font-medium cursor-pointer" data-testid="upload-cover-label">
                    <ImagePlus className="w-4 h-4" /> {uploading ? 'Mengunggah...' : (coverUrl(couple) ? 'Ganti foto' : 'Unggah foto')}
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pickCover} disabled={uploading} className="hidden" data-testid="cover-file-input" />
                  </label>
                  {coverUrl(couple) && <button onClick={removeCover} className="inline-flex items-center gap-1.5 text-xs text-red-500" data-testid="remove-cover-button"><Trash2 className="w-3.5 h-3.5" /> Hapus foto</button>}
                  <span className="text-[11px] text-sage-400">JPG/PNG/WebP, maks 5MB. Disarankan foto landscape.</span>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-sage-600">Pilih tema</Label>
              <div className="mt-2 grid gap-3">
                {Object.values(THEMES).map(th => (
                  <button key={th.id} onClick={() => setTheme(th.id)} data-testid={`theme-option-${th.id}`}
                    className={`flex items-center gap-4 p-3 rounded-2xl border text-left transition-colors ${theme === th.id ? 'border-terracotta-500 bg-terracotta-50' : 'border-cream-200 hover:bg-cream-50'}`}>
                    <div className="flex -space-x-2">{th.swatch.map((s, i) => <span key={i} className="w-7 h-7 rounded-full border-2 border-white" style={{ background: s }} />)}</div>
                    <div className="flex-1"><div className="font-medium text-sage-700 text-sm">{th.name}</div><div className="text-xs text-sage-500">{th.desc}</div></div>
                    {theme === th.id && <span className="text-[10px] font-medium text-terracotta-600 bg-white px-2 py-0.5 rounded-full">Dipilih</span>}
                  </button>
                ))}
              </div>
              <a href={`${invitationUrl(couple?.slug)}?theme=${theme}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-sm text-terracotta-500 font-medium" data-testid="preview-theme-link"><ExternalLink className="w-4 h-4" /> Pratinjau tema ini</a>
            </div>
          </div>
          <DialogFooter><Button onClick={saveTheme} data-testid="save-theme-button" className="bg-terracotta-500 hover:bg-terracotta-600 text-white">Simpan tema</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
